import React, { useEffect, useState } from 'react';
import { X, Calendar, Clock, MapPin, DollarSign, Users, ShieldAlert, Sparkles } from 'lucide-react';
import { Match, Venue, GroupSettings, MatchVisibility, MatchJoinPolicy } from '../types';
import { NumberInput } from './NumberInput';
import { formatTimeTo12Hour, formatTimeRange } from '../utils/timeUtils';

interface CreateMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  venues: Venue[];
  groupSettings: GroupSettings;
  onSaveMatch: (match: Partial<Match>) => void;
  initialMatch?: Match | null;
}

export const CreateMatchModal: React.FC<CreateMatchModalProps> = ({
  isOpen,
  onClose,
  venues,
  groupSettings,
  onSaveMatch,
  initialMatch
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Form State
  const [title, setTitle] = useState(initialMatch?.title || 'Friday Night Turf Clash');
  const [date, setDate] = useState(() => {
    if (initialMatch?.date) return initialMatch.date;
    const d = new Date();
    d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7));
    return d.toISOString().split('T')[0];
  });
  const [startTime, setStartTime] = useState(initialMatch?.startTime || '21:00');
  const [endTime, setEndTime] = useState(initialMatch?.endTime || '22:00');
  const [matchType, setMatchType] = useState<'5v5' | '6v6' | '7v7' | '8v8' | 'Custom'>(
    initialMatch?.matchType || groupSettings.matchDefaults.matchFormat || '7v7'
  );
  const [maxPlayers, setMaxPlayers] = useState(initialMatch?.maxPlayers || groupSettings.matchDefaults.playerLimit || 14);
  const [minPlayers, setMinPlayers] = useState(initialMatch?.minPlayers || groupSettings.matchDefaults.minPlayers || 10);
  
  const [selectedVenueId, setSelectedVenueId] = useState(initialMatch?.venueId || venues[0]?.id || '');
  const [customVenueName, setCustomVenueName] = useState(initialMatch?.venueName || '');
  const [customVenueAddress, setCustomVenueAddress] = useState(initialMatch?.venueAddress || '');
  const [googleMapsUrl, setGoogleMapsUrl] = useState(initialMatch?.venueMapUrl || venues[0]?.googleMapsUrl || 'https://maps.google.com');
  const [totalTurfFee, setTotalTurfFee] = useState(initialMatch?.totalTurfFee || groupSettings.finance.defaultTurfCost || 2100);
  const [organizerContribution, setOrganizerContribution] = useState(initialMatch?.organizerContribution || 0);
  const [guestFee, setGuestFee] = useState(initialMatch?.guestFee || 150);
  const [rulesNote, setRulesNote] = useState(initialMatch?.rulesNote || 'Bring both light and dark jerseys! Arrive 15 minutes before kickoff.');
  const [visibility, setVisibility] = useState<MatchVisibility>(initialMatch?.visibility || 'private');
  const [joinPolicy, setJoinPolicy] = useState<MatchJoinPolicy>(initialMatch?.joinPolicy || 'members_only');
  const [isDiscoverable, setIsDiscoverable] = useState(initialMatch?.isDiscoverable ?? false);
  const [isOpenToGuests, setIsOpenToGuests] = useState(initialMatch?.isOpenToGuests ?? groupSettings.matchDefaults.guestPlayersEnabled ?? true);

  const isCustomVenue = selectedVenueId === 'custom';
  const activeVenue = venues.find(v => v.id === selectedVenueId) || venues[0];

  const effectiveVenueName = isCustomVenue ? (customVenueName || 'Custom Turf Arena') : (activeVenue?.name || 'Dhaka Turf');
  const effectiveVenueAddress = isCustomVenue ? (customVenueAddress || 'Dhaka, Bangladesh') : (activeVenue?.address || 'Dhaka, Bangladesh');
  const effectiveMapUrl = googleMapsUrl || (isCustomVenue ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(effectiveVenueAddress)}` : (activeVenue?.googleMapsUrl || 'https://maps.google.com'));

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setTitle(initialMatch?.title || 'Friday Night Turf Clash');
    setDate(initialMatch?.date || (() => {
      const d = new Date();
      d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7));
      return d.toISOString().split('T')[0];
    })());
    setStartTime(initialMatch?.startTime || '21:00');
    setEndTime(initialMatch?.endTime || '22:00');
    setMatchType(initialMatch?.matchType || groupSettings.matchDefaults.matchFormat || '7v7');
    setMaxPlayers(initialMatch?.maxPlayers || groupSettings.matchDefaults.playerLimit || 14);
    setMinPlayers(initialMatch?.minPlayers || groupSettings.matchDefaults.minPlayers || 10);
    setSelectedVenueId(initialMatch?.venueId || venues[0]?.id || '');
    setCustomVenueName(initialMatch?.venueName || '');
    setCustomVenueAddress(initialMatch?.venueAddress || '');
    setGoogleMapsUrl(initialMatch?.venueMapUrl || venues[0]?.googleMapsUrl || 'https://maps.google.com');
    setTotalTurfFee(initialMatch?.totalTurfFee || groupSettings.finance.defaultTurfCost || 2100);
    setOrganizerContribution(initialMatch?.organizerContribution || 0);
    setGuestFee(initialMatch?.guestFee || 150);
    setRulesNote(initialMatch?.rulesNote || 'Bring both light and dark jerseys! Arrive 15 minutes before kickoff.');
    setVisibility(initialMatch?.visibility || 'private');
    setJoinPolicy(initialMatch?.joinPolicy || 'members_only');
    setIsDiscoverable(initialMatch?.isDiscoverable ?? false);
    setIsOpenToGuests(initialMatch?.isOpenToGuests ?? groupSettings.matchDefaults.guestPlayersEnabled ?? true);
  }, [isOpen, initialMatch, groupSettings, venues]);

  const estimatedCost = Math.ceil(
    (totalTurfFee - organizerContribution) / Math.max(1, maxPlayers)
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newMatch: Partial<Match> = {
      id: initialMatch?.id,
      groupId: groupSettings.id,
      title,
      date,
      startTime,
      endTime,
      durationMinutes: 60,
      matchType,
      maxPlayers,
      minPlayers,
      venueId: isCustomVenue ? `ven_custom_${Date.now()}` : (activeVenue?.id || `ven_custom_${Date.now()}`),
      venueName: effectiveVenueName,
      venueAddress: effectiveVenueAddress,
      venueMapUrl: effectiveMapUrl,
      totalTurfFee,
      organizerContribution,
      extraCost: initialMatch?.extraCost || 0,
      gkDiscount: initialMatch?.gkDiscount || 0,
      guestFee,
      estimatedCostPerPlayer: estimatedCost,
      isCostFrozen: initialMatch?.isCostFrozen || false,
      rsvpDeadline: `${date}T18:00:00Z`,
      visibility,
      joinPolicy,
      isDiscoverable,
      isOpenToGuests,
      isLocked: initialMatch?.isLocked || false,
      isCancelled: initialMatch?.isCancelled || false,
      status: initialMatch?.status || 'upcoming',
      rulesNote,
      createdAt: initialMatch?.createdAt || new Date().toISOString(),
      createdByMemberId: initialMatch?.createdByMemberId,
      createdByName: initialMatch?.createdByName,
      teamA: initialMatch?.teamA,
      teamB: initialMatch?.teamB,
      result: initialMatch?.result,
      motmWinnerMemberId: initialMatch?.motmWinnerMemberId,
      finalCostPerPlayer: initialMatch?.finalCostPerPlayer,
      customPlayerCost: initialMatch?.customPlayerCost,
    };

    onSaveMatch(newMatch);
    setStep(1);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            <h3 className="font-extrabold text-lg text-white">{initialMatch ? 'Edit Match' : 'Create New Match'}</h3>
          </div>
          <button
            onClick={() => {
              setStep(1);
              onClose();
            }}
            className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="grid grid-cols-3 bg-slate-950/50 border-b border-slate-800 text-center text-xs font-bold text-slate-400">
          <div className={`py-2 border-b-2 ${step === 1 ? 'border-emerald-400 text-emerald-400' : 'border-transparent'}`}>
            1. Details & Time
          </div>
          <div className={`py-2 border-b-2 ${step === 2 ? 'border-emerald-400 text-emerald-400' : 'border-transparent'}`}>
            2. Turf & Fee
          </div>
          <div className={`py-2 border-b-2 ${step === 3 ? 'border-emerald-400 text-emerald-400' : 'border-transparent'}`}>
            3. Rules & Confirm
          </div>
        </div>

        {/* Body Form */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 flex-1">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1">Match Title</label>
                <input
                  type="text"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 text-white font-medium rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Friday Night Turf Clash"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Match Date</label>
                  <input
                    type="date"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 text-white font-mono rounded-xl p-3 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Match Type</label>
                  <select
                    value={matchType}
                    onChange={(e) => setMatchType(e.target.value as any)}
                    className="w-full bg-slate-950 border border-slate-700 text-white font-mono rounded-xl p-3 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="5v5">5v5 (10 Players)</option>
                    <option value="6v6">6v6 (12 Players)</option>
                    <option value="7v7">7v7 (14 Players)</option>
                    <option value="8v8">8v8 (16 Players)</option>
                    <option value="Custom">Custom Format</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Start Time</label>
                  <input
                    type="time"
                    required
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 text-white font-mono rounded-xl p-3 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-[10px] text-emerald-400 font-bold block mt-1">
                    Format: {formatTimeTo12Hour(startTime)}
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">End Time</label>
                  <input
                    type="time"
                    required
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 text-white font-mono rounded-xl p-3 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-[10px] text-emerald-400 font-bold block mt-1">
                    Format: {formatTimeTo12Hour(endTime)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Max Capacity</label>
                  <NumberInput
                    value={maxPlayers}
                    onCommit={setMaxPlayers}
                    min={4}
                    max={30}
                    emptyDefault={14}
                    className="w-full bg-slate-950 border border-slate-700 text-emerald-400 font-mono font-bold rounded-xl p-3 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Min Required</label>
                  <NumberInput
                    value={minPlayers}
                    onCommit={setMinPlayers}
                    min={4}
                    max={30}
                    emptyDefault={10}
                    className="w-full bg-slate-950 border border-slate-700 text-amber-400 font-mono font-bold rounded-xl p-3 text-sm focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1">Select Turf Venue</label>
                <select
                  value={selectedVenueId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedVenueId(val);
                    if (val !== 'custom') {
                      const v = venues.find(item => item.id === val);
                      if (v) {
                        setTotalTurfFee(v.pricePerHour);
                        setGoogleMapsUrl(v.googleMapsUrl);
                      }
                    } else {
                      setGoogleMapsUrl('');
                    }
                  }}
                  className="w-full bg-slate-950 border border-slate-700 text-white font-medium rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} — {v.address} (৳{v.pricePerHour})
                    </option>
                  ))}
                  <option value="custom">📍 Custom Ground / New Map Pin Location</option>
                </select>
              </div>

              {isCustomVenue && (
                <div className="grid grid-cols-2 gap-3 bg-slate-950 p-3 rounded-2xl border border-slate-800">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Custom Turf Name</label>
                    <input
                      type="text"
                      value={customVenueName}
                      onChange={(e) => setCustomVenueName(e.target.value)}
                      placeholder="e.g. Uttara Arena Turf"
                      className="w-full bg-slate-900 border border-slate-700 text-white text-xs rounded-xl p-2.5 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">Turf Address</label>
                    <input
                      type="text"
                      value={customVenueAddress}
                      onChange={(e) => setCustomVenueAddress(e.target.value)}
                      placeholder="e.g. Sector 11, Uttara, Dhaka"
                      className="w-full bg-slate-900 border border-slate-700 text-white text-xs rounded-xl p-2.5 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Direct Google Maps Link Input */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-300 flex items-center justify-between">
                  <span className="flex items-center gap-1 text-emerald-400">
                    <MapPin className="w-3.5 h-3.5" /> Direct Google Maps Location URL:
                  </span>
                  {googleMapsUrl && (
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] text-lime-400 font-mono hover:underline flex items-center gap-1"
                    >
                      Test Map Pin ↗
                    </a>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={googleMapsUrl}
                    onChange={(e) => setGoogleMapsUrl(e.target.value)}
                    placeholder="e.g. https://maps.google.com/?q=23.8103,90.4125"
                    className="flex-1 bg-slate-950 border border-slate-700 text-slate-200 font-mono text-xs rounded-xl p-2.5 focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!googleMapsUrl && effectiveVenueAddress) {
                        const generated = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(effectiveVenueAddress)}`;
                        setGoogleMapsUrl(generated);
                      }
                    }}
                    className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-200 rounded-xl"
                  >
                    Auto Link
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Total Turf Cost (৳)</label>
                  <NumberInput
                    value={totalTurfFee}
                    onCommit={setTotalTurfFee}
                    min={0}
                    className="w-full bg-slate-950 border border-slate-700 text-white font-mono font-bold rounded-xl p-3 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Organizer Contribution</label>
                  <NumberInput
                    value={organizerContribution}
                    onCommit={setOrganizerContribution}
                    min={0}
                    className="w-full bg-slate-950 border border-slate-700 text-slate-300 font-mono rounded-xl p-3 text-sm focus:outline-none"
                  />
                </div>
              </div>

              {/* Calculated Split Card */}
              <div className="bg-emerald-950/40 border border-emerald-500/40 rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-emerald-300 block">Auto-Calculated Player Share:</span>
                  <span className="text-xs text-slate-400">Based on {maxPlayers} max confirmed players</span>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-black font-mono text-emerald-400">৳{estimatedCost}</span>
                  <span className="text-[10px] text-slate-400 block">/ player</span>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Match Visibility</label>
                  <select
                    value={visibility}
                    onChange={(e) => {
                      const nextVisibility = e.target.value as MatchVisibility;
                      setVisibility(nextVisibility);
                      if (nextVisibility === 'private') {
                        setJoinPolicy('members_only');
                        setIsDiscoverable(false);
                      } else if (nextVisibility === 'community') {
                        if (joinPolicy === 'members_only') setJoinPolicy('approval_required');
                        setIsDiscoverable(true);
                      } else {
                        setIsDiscoverable(true);
                        if (joinPolicy === 'members_only') setJoinPolicy('open_join');
                      }
                    }}
                    className="w-full bg-slate-950 border border-slate-700 text-white text-xs rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="private">Private — only your group</option>
                    <option value="community">Community — visible to trusted football network</option>
                    <option value="public">Public — anyone can discover it</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Join Rule</label>
                  <select
                    value={joinPolicy}
                    onChange={(e) => setJoinPolicy(e.target.value as MatchJoinPolicy)}
                    className="w-full bg-slate-950 border border-slate-700 text-white text-xs rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="members_only">Members only</option>
                    <option value="approval_required">Request to join (organizer approves)</option>
                    <option value="open_join">Open join if slots are available</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="bg-slate-950 border border-slate-800 rounded-2xl p-3 flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isDiscoverable}
                    onChange={(e) => setIsDiscoverable(e.target.checked)}
                    className="mt-1"
                    disabled={visibility === 'private'}
                  />
                  <div>
                    <div className="text-xs font-bold text-white">Show in discovery lists</div>
                    <div className="text-[11px] text-slate-400">Let nearby players and organizers find this match by time, area, and price.</div>
                  </div>
                </label>
                <label className="bg-slate-950 border border-slate-800 rounded-2xl p-3 flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isOpenToGuests}
                    onChange={(e) => setIsOpenToGuests(e.target.checked)}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-xs font-bold text-white">Allow guest / non-group players</div>
                    <div className="text-[11px] text-slate-400">Important for open football community mode where players can join without already being in your squad.</div>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1">Match Rules & Notes</label>
                <textarea
                  rows={3}
                  value={rulesNote}
                  onChange={(e) => setRulesNote(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 text-white text-xs rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Arrive 15 minutes before kickoff. Bring light/dark shirts."
                />
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 text-xs space-y-2">
                <div className="font-extrabold text-white text-sm">Match Summary Preview</div>
                <div className="flex justify-between text-slate-300">
                  <span>Match:</span>
                  <span className="font-bold text-emerald-400">{title}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Date & Time:</span>
                  <span className="font-mono">{date} • {startTime}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Venue:</span>
                  <span>{effectiveVenueName}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Capacity & Format:</span>
                  <span className="font-mono">{matchType} ({maxPlayers} max)</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Visibility:</span>
                  <span className="font-bold text-amber-300 capitalize">{visibility}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Join Rule:</span>
                  <span className="font-mono text-right">{joinPolicy.replaceAll('_', ' ')}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Discovery / Guests:</span>
                  <span className="text-right">{isDiscoverable ? 'Discoverable' : 'Hidden'} • {isOpenToGuests ? 'Guests OK' : 'Members only'}</span>
                </div>
                <div className="flex justify-between text-slate-300 pt-2 border-t border-slate-800">
                  <span>Est. Cost Per Player:</span>
                  <span className="font-mono font-black text-emerald-400">৳{estimatedCost}</span>
                </div>
              </div>
            </div>
          )}

          {/* Footer Controls */}
          <div className="pt-4 flex justify-between gap-3">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => setStep((step - 1) as any)}
                className="px-4 py-2.5 rounded-xl text-xs font-bold bg-slate-800 text-slate-300 hover:bg-slate-700 transition"
              >
                Back
              </button>
            ) : <div />}

            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep((step + 1) as any)}
                className="px-5 py-2.5 rounded-xl text-xs font-extrabold bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition"
              >
                Next Step
              </button>
            ) : (
              <button
                type="submit"
                className="px-6 py-2.5 rounded-xl text-xs font-black bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition shadow-lg shadow-emerald-500/20"
              >
                {initialMatch ? 'Save Match Changes' : 'Publish Match ⚽'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
